import React from "react";
import NeoCard from "../../../../../components/Common/NeoCard";
import NeoButton from "../../../../../components/Common/NeoButton";
import PlatformStageTag from "./PlatformStageTag";
import { STAGE_CONSULTANT_PLAYBOOK, getLeadScoreTier } from "../../constants";
import { getDaysSinceContact } from "../../hooks/usePlatformPipeline";

const buildWhatsAppUrl = (lead) => {
    const phone = lead.ownerPhone || lead.phone || "";
    const clean = phone.replace(/\D/g, "");
    if (!clean) return null;
    return `https://wa.me/${clean.startsWith("55") ? clean : "55" + clean}`;
};

/**
 * PlatformActionQueue — "Fila do Dia" do consultor.
 *
 * Lista os leads ativos que mais precisam de follow-up, ordenados por
 * urgência (dias sem contato) e potencial (lead score), com ações
 * de 1 clique: WhatsApp e abertura do painel do lead.
 */
const PlatformActionQueue = ({ leads = [], onOpenLead }) => {
    if (leads.length === 0) {
        return (
            <NeoCard
                title="Fila do Dia — Próximas Ações"
                subtitle="Leads priorizados por urgência e potencial de conversão."
                headerIcon="mdi-calendar-check-outline"
                thinBorder
                bodyClassName="p-4"
                className="mb-4"
            >
                <div className="text-center py-3 text-success fw-semibold">
                    <i className="mdi mdi-check-circle-outline me-1 font-size-18 align-middle" />
                    Tudo em dia! Nenhum lead ativo aguardando follow-up.
                </div>
            </NeoCard>
        );
    }

    return (
        <NeoCard
            title="Fila do Dia — Próximas Ações"
            subtitle="Leads priorizados por urgência e potencial de conversão. Comece pelo topo."
            headerIcon="mdi-calendar-check-outline"
            thinBorder
            bodyClassName="p-0"
            className="mb-4"
        >
            <div className="d-flex flex-column">
                {leads.map((lead, idx) => {
                    const days = getDaysSinceContact(lead) ?? 0;
                    const score = lead.salesData?.leadScore;
                    const tier = score !== null && score !== undefined ? getLeadScoreTier(score) : null;
                    const playbook = STAGE_CONSULTANT_PLAYBOOK[lead.salesData?.salesStage] || null;
                    const waUrl = buildWhatsAppUrl(lead);
                    const consultantName = lead.salesData?.assignedTo?.name || "Não atribuído";

                    return (
                        <div
                            key={lead.id}
                            className="d-flex align-items-center gap-3 flex-wrap px-4 py-3"
                            style={{ borderBottom: "1px solid rgba(72, 39, 18, 0.07)", cursor: "pointer" }}
                            onClick={() => onOpenLead && onOpenLead(lead)}
                            title="Abrir painel do lead"
                        >
                            {/* Posição na fila */}
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                                style={{
                                    width: "28px",
                                    height: "28px",
                                    fontSize: "12px",
                                    backgroundColor: idx === 0 ? "rgba(239, 68, 68, 0.12)" : "rgba(72, 39, 18, 0.06)",
                                    color: idx === 0 ? "#ef4444" : "var(--color-brand-primary)",
                                }}
                            >
                                {idx + 1}
                            </div>

                            {/* Lead */}
                            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                                <span className="fw-bold text-dark d-block text-truncate">{lead.name}</span>
                                <span className="text-xs text-muted d-block text-truncate">
                                    <i className="mdi mdi-account-outline me-1" />
                                    {lead.ownerName || "Dono não informado"} · {consultantName}
                                </span>
                            </div>

                            {/* Estágio */}
                            <div className="flex-shrink-0">
                                <PlatformStageTag stageKey={lead.salesData?.salesStage} />
                            </div>

                            {/* Score */}
                            {tier && (
                                <span
                                    className="badge fw-bold flex-shrink-0"
                                    style={{ color: tier.color, backgroundColor: tier.bg }}
                                    title={`Lead Score: ${score}/100 — ${tier.label}`}
                                >
                                    {score} pts
                                </span>
                            )}

                            {/* Dias sem contato */}
                            <span
                                className={`badge fw-semibold flex-shrink-0 ${days >= 3 ? "bg-soft-danger text-danger" : "bg-soft-warning text-warning"}`}
                                title="Dias desde o último contato registrado"
                            >
                                <i className="mdi mdi-clock-alert-outline me-1" />
                                {days}d sem contato
                            </span>

                            {/* Dica do playbook */}
                            {playbook && (
                                <span
                                    className="text-xs text-muted d-none d-xl-block text-truncate"
                                    style={{ maxWidth: "260px" }}
                                    title={`${playbook.goal} — ${playbook.action}`}
                                >
                                    <i className="mdi mdi-lightbulb-on-outline me-1 text-warning" />
                                    {playbook.sla}
                                </span>
                            )}

                            {/* Ações */}
                            <div className="d-flex gap-2 ms-auto flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {waUrl && (
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-soft-success fw-semibold"
                                        title="Chamar no WhatsApp"
                                    >
                                        <i className="mdi mdi-whatsapp me-1" />
                                        WhatsApp
                                    </a>
                                )}
                                <NeoButton
                                    variant="soft-primary"
                                    size="sm"
                                    onClick={() => onOpenLead && onOpenLead(lead)}
                                >
                                    Registrar Contato
                                </NeoButton>
                            </div>
                        </div>
                    );
                })}
            </div>
        </NeoCard>
    );
};

export default PlatformActionQueue;
